import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { NgFor, NgIf } from "@angular/common";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { forkJoin, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { MatButtonModule } from "@angular/material/button";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatNativeDateModule } from "@angular/material/core";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatListModule, MatSelectionListChange } from "@angular/material/list";
import { MatSelectModule } from "@angular/material/select";
import swal from "sweetalert2";
import { Area } from "src/app/shared/model/empleado/area.model";
import { Empleado } from "src/app/shared/model/empleado/empleado.model";
import { Puesto } from "src/app/shared/model/empleado/puesto.model";
import { Perfil } from "src/app/shared/model/security/perfil.model";
import { Persona } from "src/app/shared/model/security/persona.model";
import { Sitios } from "src/app/shared/model/security/sitios.model";
import { Usuario } from "src/app/shared/model/security/usuario.model";
import { ConsultaList } from "src/app/shared/model/core/ConsultaList.model";
import { EmpleadoService } from "../empleado.service";
import { PinRestablecerComponent } from "../../security/pin-autorizador/pin-restablecer.component";

type GrupoPerfiles = {
  sitio: string;
  perfiles: Perfil[];
};

type EmpleadoListState = {
  currentPage?: number;
  pageSize?: number;
  searchTerm?: string;
};

@Component({
  selector: "app-modal-empleado",
  standalone: true,
  templateUrl: "./modal-empleado.component.html",
  styleUrls: ["./modal-empleado.component.css"],
  imports: [
    NgFor,
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatSelectModule,
    PinRestablecerComponent,
  ],
})
export class ModalEmpleadoComponent implements OnInit {
  formGroupEmpleado!: FormGroup;
  formGroupUsuario!: FormGroup;
  formGroupPerfil!: FormGroup;
  listPerfiles: Perfil[] = [];
  gruposPerfiles: GrupoPerfiles[] = [];
  listPerfilesEmpleado: Perfil[] = [];
  listArea: Area[] = [];
  listPuestos: Puesto[] = [];
  perfilesValidos = false;
  tituloOperacion = "Alta";
  maxDate: Date = new Date();
  objectEmpleado: Empleado = this.createEmpleadoBase();
  isEditMode = false;
  isSaving = false;
  /** Nombre del sitio biometrico tal como lo espera el backend (adm_perfil.SITIO). */
  private static readonly SITIO_BIOMETRICOS = "BIOMETRICOS";
  /** Centinela de negacion del kiosco, en minusculas para comparar. */
  private static readonly PERFIL_NO_APLICA = "no aplica";
  private readonly listRoute = "/EMPLEADO/GESTION_EMPLEADOS";
  private listState: EmpleadoListState | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private empleadoService: EmpleadoService,
    private cdref: ChangeDetectorRef
  ) {}

  get formUsuar() {
    return this.formGroupUsuario.controls;
  }

  get selectedProfilesCount(): number {
    const perfilesSeleccionados =
      this.formGroupPerfil?.controls?.controlPerfiles?.value;
    return Array.isArray(perfilesSeleccionados) ? perfilesSeleccionados.length : 0;
  }

  get nombreEmpleadoPreview(): string {
    const partes = [
      this.objectEmpleado?.persona?.nombre,
      this.objectEmpleado?.persona?.apellidoPaterno,
      this.objectEmpleado?.persona?.apellidoMaterno,
    ]
      .map((value) => (typeof value === "string" ? value.trim() : ""))
      .filter((value) => value.length > 0);

    return partes.length > 0 ? partes.join(" ") : "Pendiente de captura";
  }

  get areaLabel(): string {
    const areaSeleccionada = this.listArea.find(
      (area) => area.id === this.objectEmpleado?.puesto?.id_area
    );
    return areaSeleccionada?.area || "Sin asignar";
  }

  get puestoLabel(): string {
    const puestoSeleccionado = this.listPuestos.find(
      (puesto) => puesto.id === this.objectEmpleado?.idPuesto
    );
    return puestoSeleccionado?.puesto || "Sin asignar";
  }

  get statusLabel(): string {
    if (!this.isEditMode) {
      return "Nuevo registro";
    }

    return this.objectEmpleado?.usuario?.estatus === "ACTIVO"
      ? "Activo"
      : "Inactivo";
  }

  ngOnInit() {
    if (!this.initializeRouteContext()) {
      return;
    }

    this.initializeForms();
    this.pintaPerfiles();
    this.consultaAreas();
  }

  pintaPerfiles(): void {
    // El catalogo de perfiles se arma con dos consultas porque el backend las
    // sirve por ramas excluyentes: la de sesion trae los perfiles de los sitios
    // vendidos al cliente, y BIOMETRICOS hay que pedirlo por nombre porque no
    // cuelga de ninguna venta y nunca viaja en la lista de sitios de la sesion.
    forkJoin({
      sesion: this.empleadoService.consultaPerfiles(new Sitios()),
      biometricos: this.empleadoService
        .consultaPerfiles(this.sitioBiometrico())
        // Si el catalogo biometrico falla, la pantalla sigue sirviendo para el
        // resto de los perfiles en lugar de quedarse vacia.
        .pipe(catchError(() => of({ list: [] } as ConsultaList<Perfil>))),
    }).subscribe(({ sesion, biometricos }) => {
      this.listPerfiles = this.mezclaPerfiles(sesion?.list, biometricos?.list);
      this.gruposPerfiles = this.agrupaPerfilesPorSitio(this.listPerfiles);
      this.perfilesValidos = this.selectedProfilesCount > 0;

      if (this.isEditMode && this.objectEmpleado.usuario.id != null) {
        this.consultaPerfilesEmpleado(this.objectEmpleado.usuario.id);
        return;
      }

      const perfilesSeleccionados = this.normalizaExclusividad(
        this.listPerfiles.filter((perfil) =>
          this.objectEmpleado.listaPerfiles.some((perfilEmpleado) => perfilEmpleado.id === perfil.id)
        )
      );
      this.formGroupPerfil.controls.controlPerfiles.setValue(perfilesSeleccionados);
      this.perfilesValidos =
        Array.isArray(perfilesSeleccionados) && perfilesSeleccionados.length > 0;
      this.cdref.detectChanges();
    });
  }

  get hayCatalogoBiometrico(): boolean {
    return this.listPerfiles.some((perfil) => this.esNegacionBiometrica(perfil));
  }

  esPerfilBiometrico(perfil: Perfil): boolean {
    return (
      (perfil?.sitio || "").trim().toUpperCase() ===
      ModalEmpleadoComponent.SITIO_BIOMETRICOS
    );
  }

  private sitioBiometrico(): Sitios {
    const sitio = new Sitios();
    sitio.nombre = ModalEmpleadoComponent.SITIO_BIOMETRICOS;
    return sitio;
  }

  /**
   * Une los dos catalogos sin repetir. Se compara por id porque un mismo perfil
   * podria llegar por ambas vias si algun dia BIOMETRICOS formara parte de una
   * venta, y una entrada duplicada rompe la preseleccion del mat-selection-list,
   * que casa las opciones por referencia.
   */
  private mezclaPerfiles(...catalogos: Array<Perfil[] | undefined>): Perfil[] {
    const perfiles: Perfil[] = [];
    const idsAgregados = new Set<unknown>();

    for (const catalogo of catalogos) {
      if (!Array.isArray(catalogo)) {
        continue;
      }
      for (const perfil of catalogo) {
        if (!perfil || idsAgregados.has(perfil.id)) {
          continue;
        }
        idsAgregados.add(perfil.id);
        perfiles.push(perfil);
      }
    }

    return perfiles;
  }

  private agrupaPerfilesPorSitio(perfiles: Perfil[]): GrupoPerfiles[] {
    const grupos: GrupoPerfiles[] = [];

    for (const perfil of perfiles) {
      const sitio = (perfil?.sitio || "").trim() || "SIN SITIO";
      const grupoExistente = grupos.find((grupo) => grupo.sitio === sitio);

      if (grupoExistente) {
        grupoExistente.perfiles.push(perfil);
        continue;
      }

      grupos.push({ sitio, perfiles: [perfil] });
    }

    return grupos;
  }

  validaPerfiles(cambio?: MatSelectionListChange): void {
    this.aplicaExclusividadDeNoAplica(cambio);
    this.perfilesValidos = this.selectedProfilesCount > 0;
  }

  /**
   * 'No aplica' es la negacion explicita del acceso al kiosco, y en el sitio
   * BIOMETRICOS es EXCLUYENTE: no convive con ningun otro perfil biometrico.
   * Los dos clientes del kiosco ya lo imponen en su propia UI (CosFxSolser
   * ModalFormController y CosBiometrico employee_form_dialog), asi que esta
   * pantalla tiene que comportarse igual.
   *
   * Si no, el admin podria marcar 'No aplica' junto a 'Administrador' creyendo
   * que revoca el permiso, y el kiosco seguiria dejando entrar: su regla es
   * "existe ALGUN perfil biometrico distinto de No aplica".
   */
  private aplicaExclusividadDeNoAplica(cambio?: MatSelectionListChange): void {
    const seleccion: Perfil[] =
      this.formGroupPerfil.controls.controlPerfiles.value || [];

    const recienMarcados: Perfil[] = (cambio?.options || [])
      .filter((opcion) => opcion.selected)
      .map((opcion) => opcion.value as Perfil);

    let filtrada = seleccion;

    if (recienMarcados.some((perfil) => this.esNegacionBiometrica(perfil))) {
      // Se acaba de marcar 'No aplica': apaga el resto del sitio biometrico.
      filtrada = this.normalizaExclusividad(seleccion);
    } else if (
      recienMarcados.some(
        (perfil) => this.esPerfilBiometrico(perfil) && !this.esNegacionBiometrica(perfil)
      )
    ) {
      // Se acaba de marcar un perfil biometrico real: apaga 'No aplica'.
      filtrada = seleccion.filter((perfil) => !this.esNegacionBiometrica(perfil));
    }

    if (filtrada.length !== seleccion.length) {
      this.formGroupPerfil.controls.controlPerfiles.setValue(filtrada);
    }
  }

  /**
   * Deja una seleccion coherente con la exclusividad de 'No aplica'.
   *
   * Se aplica tambien a la seleccion que llega PRECARGADA desde la base: el
   * catalogo biometrico se capturo a mano fuera del control de versiones, asi
   * que la combinacion contradictoria puede existir ya. Si solo se corrigiera
   * al hacer clic, el admin veria las dos casillas marcadas y al guardar las
   * volveria a escribir tal cual.
   */
  private normalizaExclusividad(seleccion: Perfil[]): Perfil[] {
    if (!seleccion.some((perfil) => this.esNegacionBiometrica(perfil))) {
      return seleccion;
    }
    return seleccion.filter(
      (perfil) => !this.esPerfilBiometrico(perfil) || this.esNegacionBiometrica(perfil)
    );
  }

  private esNegacionBiometrica(perfil: Perfil): boolean {
    return (
      this.esPerfilBiometrico(perfil) &&
      (perfil?.nombre || "").trim().toLowerCase() ===
        ModalEmpleadoComponent.PERFIL_NO_APLICA
    );
  }

  validaPassword(controlPass: string, controlRepass: string) {
    return (formGroup: FormGroup) => {
      const control = formGroup.controls[controlPass];
      const matchingControl = formGroup.controls[controlRepass];

      if (matchingControl.errors && !matchingControl.errors.mustMatch) {
        return;
      }
      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ mustMatch: true });
      } else {
        matchingControl.setErrors(null);
      }
    };
  }

  consultaAreas(): void {
    this.empleadoService.consultaAreas().subscribe((res) => {
      this.listArea = Array.isArray(res?.list) ? res.list : [];
      const currentAreaId = this.objectEmpleado?.puesto?.id_area;

      if (currentAreaId != null) {
        this.formGroupEmpleado.controls.controlArea.setValue(currentAreaId);
        this.consultaPuestos(currentAreaId);
        return;
      }

      if (this.listArea.length === 1) {
        const areaId = this.listArea[0].id;
        this.objectEmpleado.puesto.id_area = areaId;
        this.formGroupEmpleado.controls.controlArea.setValue(areaId);
        this.consultaPuestos(areaId);
      }
    });
  }

  onAreaChange(idArea: number): void {
    this.objectEmpleado.puesto.id_area = idArea;
    this.objectEmpleado.idPuesto = null as unknown as number;
    this.formGroupEmpleado.controls.controlPuesto.setValue(null);
    this.consultaPuestos(idArea);
  }

  consultaPuestos(idArea: number): void {
    if (idArea == undefined || idArea == null) {
      this.listPuestos = [];
      return;
    }

    this.empleadoService.consultaPuestosArea(idArea).subscribe((res) => {
      this.listPuestos = Array.isArray(res?.list) ? res.list : [];
      const puestoActualValido = this.listPuestos.some(
        (puesto) => puesto.id === this.objectEmpleado.idPuesto
      );

      if (!puestoActualValido) {
        this.objectEmpleado.idPuesto =
          this.listPuestos.length === 1
            ? this.listPuestos[0].id
            : (null as unknown as number);
      }

      this.formGroupEmpleado.controls.controlPuesto.setValue(
        this.objectEmpleado.idPuesto ?? null
      );
      this.cdref.detectChanges();
    });
  }

  consultaPerfilesEmpleado(idEmpleado: number): void {
    this.empleadoService.consultaPerfilesEmpleado(idEmpleado).subscribe((res) => {
      this.objectEmpleado.listaPerfiles = Array.isArray(res?.list) ? res.list : [];
      this.listPerfilesEmpleado = this.objectEmpleado.listaPerfiles;

      for (const perfilAux of this.listPerfilesEmpleado) {
        for (const perfilAux2 of this.listPerfiles) {
          if (perfilAux.id === perfilAux2.id) {
            perfilAux2.select = true;
          }
        }
      }

      const perfilesSeleccionados = this.normalizaExclusividad(
        this.listPerfiles.filter((perfil) => perfil.select)
      );
      this.formGroupPerfil.controls.controlPerfiles.setValue(perfilesSeleccionados);
      this.perfilesValidos = perfilesSeleccionados.length > 0;
      this.cdref.detectChanges();
    });
  }

  onClicCerrarModal(): void {
    this.navigateBackToList();
  }

  onClicGuardar(): void {
    if (
      this.formGroupEmpleado.invalid ||
      this.formGroupUsuario.invalid ||
      !this.perfilesValidos ||
      this.isSaving
    ) {
      this.formGroupEmpleado.markAllAsTouched();
      this.formGroupUsuario.markAllAsTouched();
      this.formGroupPerfil.markAllAsTouched();
      return;
    }

    if (this.formGroupUsuario.controls.controlPass.pristine && this.isEditMode) {
      this.objectEmpleado.usuario.password = null as unknown as string;
    } else {
      this.objectEmpleado.usuario.password =
        this.objectEmpleado.usuario.password?.trim() || "";
    }

    this.objectEmpleado.listaPerfiles =
      this.formGroupPerfil.controls.controlPerfiles.value || [];
    this.isSaving = true;

    this.empleadoService
      .guardaActualizaEmpleado(this.objectEmpleado)
      .subscribe({
        next: (response) => {
          const success = response.pCodRetorno === "00000";
          this.isSaving = false;

          swal
            .fire({
              title: success ? "ÉXITO" : "ERROR",
              text: response.pMensaje,
              buttonsStyling: false,
              customClass: {
                confirmButton: "btn btn-success",
                cancelButton: "btn btn-danger",
              },
              confirmButtonText: "Aceptar",
              icon: success ? "success" : "warning",
            })
            .then(() => {
              if (success) {
                this.navigateBackToList();
              }
            });
        },
        error: () => {
          this.isSaving = false;
          swal.fire({
            title: "ERROR",
            text: "No fue posible guardar la información del empleado.",
            buttonsStyling: false,
            customClass: {
              confirmButton: "btn btn-danger",
            },
            confirmButtonText: "Aceptar",
            icon: "error",
          });
        },
      });
  }

  private initializeRouteContext(): boolean {
    this.listState = (history.state?.listState as EmpleadoListState) || null;
    this.isEditMode = this.route.snapshot.paramMap.has("id");
    const empleadoState = history.state?.empleado as Empleado | undefined;

    if (this.isEditMode) {
      if (!empleadoState) {
        queueMicrotask(() => {
          swal
            .fire({
              title: "Información no disponible",
              text: "Seleccione nuevamente el empleado desde la gestión para editarlo.",
              buttonsStyling: false,
              customClass: {
                confirmButton: "btn btn-success",
              },
              confirmButtonText: "Aceptar",
              icon: "info",
            })
            .then(() => this.navigateBackToList());
        });
        return false;
      }

      this.objectEmpleado = this.hydrateEmpleado(empleadoState);
      this.tituloOperacion = "Edición";
      this.objectEmpleado.usuario.password = "******";
      return true;
    }

    this.objectEmpleado = this.createEmpleadoBase();
    this.tituloOperacion = "Alta";
    return true;
  }

  private initializeForms(): void {
    this.formGroupEmpleado = this.formBuilder.group({
      controlNombre: [this.objectEmpleado.persona.nombre || "", Validators.required],
      controlAPaterno: [
        this.objectEmpleado.persona.apellidoPaterno || "",
        Validators.required,
      ],
      controlAMaterno: [
        this.objectEmpleado.persona.apellidoMaterno || "",
        Validators.required,
      ],
      controlEmail: [
        this.objectEmpleado.persona.correoelectronico || "",
        [Validators.required, Validators.email],
      ],
      controlArea: [this.objectEmpleado.puesto.id_area ?? null, Validators.required],
      controlFechaNac: [this.objectEmpleado.persona.fechaNacimiento || ""],
      controlPuesto: [this.objectEmpleado.idPuesto ?? null, Validators.required],
    });

    this.formGroupUsuario = this.formBuilder.group(
      {
        controlUsuario: [
          this.objectEmpleado.usuario.user_nom || "",
          [Validators.required, Validators.minLength(5)],
        ],
        controlPass: [
          this.objectEmpleado.usuario.password || "",
          [Validators.required, Validators.minLength(6)],
        ],
        controlRePass: [
          this.objectEmpleado.usuario.password || "",
          [Validators.required, Validators.minLength(6)],
        ],
      },
      {
        validators: this.validaPassword("controlPass", "controlRePass"),
      }
    );

    this.formGroupPerfil = this.formBuilder.group({
      controlPerfiles: [this.objectEmpleado.listaPerfiles || []],
    });

    if (this.isEditMode) {
      this.formGroupUsuario.controls.controlUsuario.disable();
    }

    this.perfilesValidos =
      Array.isArray(this.objectEmpleado.listaPerfiles) &&
      this.objectEmpleado.listaPerfiles.length > 0;
  }

  private navigateBackToList(): void {
    this.router.navigate([this.listRoute], {
      state: this.listState ? { listState: this.listState } : undefined,
    });
  }

  private createEmpleadoBase(): Empleado {
    const empleado = new Empleado();
    empleado.persona = new Persona();
    empleado.usuario = new Usuario();
    empleado.puesto = new Puesto();
    empleado.listaPerfiles = [];
    return empleado;
  }

  private hydrateEmpleado(source: Empleado): Empleado {
    const empleado = this.createEmpleadoBase();

    Object.assign(empleado, source || {});
    empleado.persona = Object.assign(new Persona(), source?.persona || {});
    empleado.usuario = Object.assign(new Usuario(), source?.usuario || {});
    empleado.puesto = Object.assign(new Puesto(), source?.puesto || {});
    empleado.listaPerfiles = Array.isArray(source?.listaPerfiles)
      ? source.listaPerfiles.map((perfil) => Object.assign(new Perfil(), perfil))
      : [];

    return empleado;
  }
}
